import { Component } from '@theme/component';

/**
 * <zerbay-recommendations> loads the "You may also like" strip (PDPM-21).
 *
 * The shell render of sections/zerbay-recommendations.liquid prints the heading and a list of 4 skeleton cards. On connect
 * (never on scroll or intersection) this element fetches the section's own endpoint render (data-url), swaps the fresh card
 * list in for the skeleton list and clears aria-busy. Every exit leaves no skeleton behind: the list is replaced (success) or
 * the whole element gets `hidden` (empty answer, HTTP error, network error, parse error, 8 s timeout).
 *
 * Contract with the section:
 *   - root attribute data-url: the recommendations endpoint URL (locale prefix kept)
 *   - [data-zb-recs-cards]: the card list, in the shell (skeletons) and in the endpoint answer (cards as <li>)
 *
 * Rules: one 8000 ms timer starts BEFORE the request; on expiry the request is aborted and the section hidden; a late answer
 * is ignored (single finish flag). The fresh list gets min-height = the skeleton list height, so the section never shrinks.
 */
/** @typedef {{ controller: AbortController, timer: number | null, done: boolean }} Run */

const TIMEOUT_MS = 8000;
const INIT_ATTRIBUTE = 'data-zb-recs-init';
const CARDS_SELECTOR = '[data-zb-recs-cards]';

class ZerbayRecommendations extends Component {
  /**
   * The active run. Every start creates a new object; timer, response and error handlers act only while
   * `this.#run` is still the run they belong to, so a stale abort or a late answer can never touch a newer run.
   * @type {Run | null}
   */
  #run = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute(INIT_ATTRIBUTE)) return;
    this.setAttribute(INIT_ATTRIBUTE, '1');
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const run = this.#run;
    if (run && !run.done) {
      // Invalidate first, then abort: the aborted request's catch handler sees a different (or no) active run.
      this.#run = null;
      if (run.timer !== null) window.clearTimeout(run.timer);
      run.controller.abort();
      this.removeAttribute(INIT_ATTRIBUTE);
    }
  }

  /**
   * Ends a run once; calls from a run that is no longer active, or a second call, do nothing.
   * @param {Run} run
   * @param {boolean} hide - hide the whole section
   * @returns {boolean} whether this call finished the run
   */
  #finish(run, hide) {
    if (this.#run !== run || run.done) return false;
    run.done = true;
    if (run.timer !== null) {
      window.clearTimeout(run.timer);
      run.timer = null;
    }
    if (hide) this.hidden = true;
    this.setAttribute('aria-busy', 'false');
    return true;
  }

  #start() {
    const controller = new AbortController();
    const run = { controller, timer: /** @type {number | null} */ (null), done: false };
    this.#run = run;
    this.hidden = false;
    this.setAttribute('aria-busy', 'true');

    run.timer = window.setTimeout(() => {
      run.timer = null;
      if (this.#finish(run, true)) controller.abort();
    }, TIMEOUT_MS);

    const url = this.dataset.url;
    if (!url) {
      this.#finish(run, true);
      return;
    }

    fetch(url, { credentials: 'same-origin', priority: 'low', signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`zerbay-recommendations ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (this.#run !== run || run.done) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = doc.querySelector(CARDS_SELECTOR);
        const track = this.querySelector(CARDS_SELECTOR);
        const cards = list ? Array.from(list.children).filter((child) => child.tagName === 'LI').length : 0;
        if (!list || !track || cards === 0) {
          this.#finish(run, true);
          return;
        }
        for (const script of list.querySelectorAll('script')) script.remove();
        const fresh = document.importNode(list, true);
        // The section never gets shorter when the cards replace the placeholders.
        fresh.style.minHeight = `${Math.ceil(track.getBoundingClientRect().height)}px`;
        track.replaceWith(fresh);
        this.#finish(run, false);
      })
      .catch(() => {
        this.#finish(run, true);
      });
  }
}

if (!customElements.get('zerbay-recommendations')) {
  customElements.define('zerbay-recommendations', ZerbayRecommendations);
}
